import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import './AsistenteWhatsApp.css';

/**
 * El estado del asistente de WhatsApp. Reemplaza al formulario de 5 pasos.
 *
 * POR QUÉ NO HAY FORMULARIO
 * El asistente anterior pedía otra vez el número, el saludo y los horarios —y
 * después ejecutaba su propia máquina de estados en el navegador—. Todo eso ya
 * vive en otro lado: los servicios, el equipo y los horarios están cargados en
 * esta misma cuenta, y el asistente los lee de ahí en cada conversación.
 *
 * Pedirlos de nuevo tiene un costo que no se ve hasta que duele: son los
 * mismos datos en dos lugares, y el día que se contradicen el asistente dice
 * un precio y el local cobra otro. Por eso esta pantalla MUESTRA, no pregunta.
 *
 * Lo único que no puede salir de la cuenta es a qué número le escriben los
 * clientes, y eso lo dice el asistente.
 */

const AsistenteWhatsApp = () => {
  const { currentUser } = useAuth();
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [copiado, setCopiado] = useState(false);

  const traer = useCallback(async () => {
    try {
      const token = await currentUser.getIdToken();
      const r = await fetch(`${API_URL}/api/whatsapp/asistente/estado`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('No se pudo consultar el asistente');
      setEstado(await r.json());
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [currentUser]);

  useEffect(() => { traer(); }, [traer]);

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch (e) { /* sin portapapeles, el número igual se ve y se puede copiar a mano */ }
  };

  if (cargando) return <p className="asis__cargando">Consultando el asistente…</p>;

  const conectado = estado?.conectado;

  return (
    <div className="asis">
      <header className="asis__cabecera">
        <div>
          <h3>Tu asistente de WhatsApp</h3>
          <p>
            Contesta preguntas y saca turnos solo, con los servicios y horarios
            que ya tenés cargados. No hay nada que configurar acá.
          </p>
        </div>
        <span className={`asis__chip ${conectado ? 'esta-ok' : 'esta-mal'}`}>
          {conectado ? 'Funcionando' : 'Sin conexión'}
        </span>
      </header>

      {error && <p className="asis__error">{error}</p>}

      {!conectado && (
        <div className="asis__aviso">
          <strong>El asistente no está respondiendo.</strong>
          <p>
            {estado?.detalle || 'No pudimos contactarlo. Si acabás de activarlo, ' +
              'puede tardar un minuto en arrancar.'}
          </p>
          <button type="button" onClick={traer}>Reintentar</button>
        </div>
      )}

      {conectado && (
        <>
          <div className="asis__numero">
            <span className="asis__etiqueta">Tus clientes le escriben a</span>
            <div className="asis__numero-fila">
              <strong>{estado.numero}</strong>
              <button type="button" onClick={() => copiar(estado.numero)}>
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </div>

          {estado.sandbox && (
            <div className="asis__aviso asis__aviso--tibio">
              <strong>Es un número de prueba, compartido.</strong>
              <p>
                Antes de poder escribirle, cada persona tiene que mandarle
                <code> join {estado.codigoSandbox || '<tu código>'} </code>
                por WhatsApp. Sirve para probarlo entre ustedes, pero no para
                darle el número a un cliente: sin ese paso, no le va a contestar.
              </p>
              <p className="asis__nota">
                Con un número propio esto desaparece y el asistente contesta al
                primer mensaje.
              </p>
            </div>
          )}

          <div className="asis__sabe">
            <span className="asis__etiqueta">Lo que ya sabe de tu negocio</span>
            <ul>
              <li>
                <strong>{estado.servicios}</strong>{' '}
                {estado.servicios === 1 ? 'servicio' : 'servicios'} con precio y duración
              </li>
              <li>
                <strong>{estado.staff}</strong>{' '}
                {estado.staff === 1 ? 'profesional' : 'profesionales'} con su agenda
              </li>
              <li>Tus horarios de atención, incluidos los días cerrados</li>
              <li>Los turnos que ya tenés tomados</li>
            </ul>
            <p className="asis__nota">
              Todo sale de tu cuenta en cada conversación. Si cambiás un precio o
              un horario, el asistente lo dice bien desde ese momento — no hay
              que avisarle.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AsistenteWhatsApp;
